import { Fragment, useCallback, type FC, type PropsWithChildren } from 'react';
import { useNavigate } from 'react-router';
import { Check } from '@mui/icons-material';
import { IconButton } from '@mui/material';

import { NestedPageHeader } from '#packages/web/layouts/page/header';
import {
  PageContent,
  type PageContentProps,
} from '#packages/web/layouts/page/page-content';
import { useDialog } from '#packages/web/hooks/use-dialog';
import { DiscardChangesConfirmationDialog } from '#packages/web/layouts/dialog/templates';

import { PageErrorBoundary } from './page-error-boundary';

export type FormPageLayoutProps = {
  /**
   * Title to display at the top of the page.
   */
  title: string;

  /**
   * Props to pass to the PageContent component.
   */
  pageContentProps?: Partial<PageContentProps>;

  /**
   * Callback when the error boundary action button is clicked.
   */
  onErrorActionClick?: () => void;

  /**
   * Indicates whether the form has unsaved changes (used to prompt the user before navigating away).
   */
  isFormChanged: boolean;

  /**
   * Form submission handler.
   */
  onSubmit: () => void;

  /**
   * Indicates whether the submit action/button should be disabled.
   */
  isSubmitDisabled: boolean;

  /**
   * Optional header children to render inside the header.
   */
  headerChildren?: React.ReactNode;
} & React.FormHTMLAttributes<HTMLFormElement>;

export const FormPageLayout: FC<PropsWithChildren<FormPageLayoutProps>> = ({
  title,

  pageContentProps,
  onErrorActionClick = useCallback(() => window.location.reload(), []),

  isFormChanged,
  onSubmit,
  isSubmitDisabled,

  headerChildren,

  children,
}) => {
  const navigate = useNavigate();
  const confirmDiscardChangesDialog = useDialog('closed');

  const goBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const onNavigationIconClick = useCallback(() => {
    // If the form is not changed, navigate back immediately
    if (!isFormChanged) {
      return goBack();
    }

    // Show confirmation dialog
    return confirmDiscardChangesDialog.open();
  }, [goBack, confirmDiscardChangesDialog.open, isFormChanged]);

  const onDiscardChanges = useCallback(() => {
    confirmDiscardChangesDialog.close();
    goBack();
  }, [goBack, confirmDiscardChangesDialog.close]);

  return (
    <Fragment>
      <NestedPageHeader
        title={title}
        onNavigationIconClick={onNavigationIconClick}>
        <IconButton
          color="inherit"
          aria-label="save"
          edge="start"
          sx={{ mr: 0.5 }}
          onClick={onSubmit}
          disabled={isSubmitDisabled}>
          <Check fontSize="large" />
        </IconButton>
        {headerChildren}
      </NestedPageHeader>
      <PageContent {...pageContentProps}>
        <PageErrorBoundary onActionClick={onErrorActionClick}>
          {/* The page content */}
          {children}

          {/* Confirmation dialog */}
          {confirmDiscardChangesDialog.isOpen && (
            <DiscardChangesConfirmationDialog
              open
              onConfirm={onDiscardChanges}
              onCancel={confirmDiscardChangesDialog.close}
            />
          )}
        </PageErrorBoundary>
      </PageContent>
    </Fragment>
  );
};
